import { Controller } from "@hotwired/stimulus"

// Floating "Insert tag" toolbar — port of the legacy editor's
// #param-tag-toolbar (editor_source/pcp_builder.html). Appears at the
// bottom-center of the screen whenever a free-text field is focused and
// lets the facilitator drop a participant placeholder token
// ([FirstName], [LastName], [Age], [Location], [School], [Grade]) at the
// caret. The insert dispatches input+change so the section controllers'
// autosave and TBD / name-usage detection pick it up exactly like a
// typed edit.
//
// The element carries data-action="mousedown->param-tag-toolbar#preventBlur"
// so pressing a chip doesn't blur the field before the click lands. The
// chip buttons carry data-ptt="[Tag]" + data-action="…#insert"; the close
// button carries data-action="…#close".

const TEXT_INPUT_TYPES = new Set(["text", "search", "url", "email", "tel", ""])

export default class extends Controller {
  connect() {
    this._lastField = null
    this._selStart  = 0
    this._selEnd    = 0
    this._dismissed = false   // user pressed × / Esc — re-armed on next focus

    this._onFocusIn = (e) => {
      const el = e.target
      if (this._isEligible(el)) {
        this._lastField = el
        this._remember(el)
        if (!this._dismissed) this._show()
      }
    }
    // Defer the hide so a click on a chip (which shifts focus) still fires
    // before the bar disappears.
    this._onFocusOut = () => {
      setTimeout(() => {
        const a = document.activeElement
        if (!this._isEligible(a) && !(a && a.closest && a.closest("#en-param-tag-toolbar"))) {
          this._hide()
        }
      }, 30)
    }
    this._onKeyUp    = (e) => { if (e.target === this._lastField) this._remember(e.target) }
    this._onMouseUp  = (e) => { if (e.target === this._lastField) this._remember(e.target) }
    this._onSelChange = () => {
      const a = document.activeElement
      if (a && a === this._lastField && this._isEligible(a)) this._remember(a)
    }
    this._onKeyDown = (e) => {
      if (e.key === "Escape" && this.element.classList.contains("is-visible")) {
        this._dismissed = true
        this._hide()
      }
    }

    document.addEventListener("focusin",  this._onFocusIn,  true)
    document.addEventListener("focusout", this._onFocusOut, true)
    document.addEventListener("keyup",    this._onKeyUp,    true)
    document.addEventListener("mouseup",  this._onMouseUp,  true)
    document.addEventListener("selectionchange", this._onSelChange)
    document.addEventListener("keydown",  this._onKeyDown)
  }

  disconnect() {
    document.removeEventListener("focusin",  this._onFocusIn,  true)
    document.removeEventListener("focusout", this._onFocusOut, true)
    document.removeEventListener("keyup",    this._onKeyUp,    true)
    document.removeEventListener("mouseup",  this._onMouseUp,  true)
    document.removeEventListener("selectionchange", this._onSelChange)
    document.removeEventListener("keydown",  this._onKeyDown)
  }

  // Keep the field from losing focus when a chip is pressed.
  preventBlur(e) { e.preventDefault() }

  insert(e) {
    const tag = e.currentTarget.dataset.ptt
    const f = this._lastField
    if (!tag || !f) return
    const v      = f.value || ""
    const before = v.slice(0, this._selStart)
    const after  = v.slice(this._selEnd)
    f.value = before + tag + after
    const caret = (before + tag).length
    f.focus()
    try { f.setSelectionRange(caret, caret) } catch (_) {}
    // Fire the same events a real edit would so autosave + TBD / name
    // detection run.
    f.dispatchEvent(new Event("input",  { bubbles: true }))
    f.dispatchEvent(new Event("change", { bubbles: true }))
    this._remember(f)
  }

  // Wrap the field's current selection in a //instruction// marker — or drop
  // an empty "//  //" at the caret when nothing is selected — so the
  // facilitator can tell the AI proofreader to APPLY a change to that text
  // (e.g. //make this more concise//) instead of only proofreading it. The
  // ai-proofread export explains the marker to the AI, and its import strips
  // any leftover marker. Same input+change dispatch as insert() so autosave
  // and TBD / name detection run exactly like a typed edit.
  insertInstruction(e) {
    e?.preventDefault?.()
    const f = this._lastField
    if (!f) return
    const v        = f.value || ""
    const before   = v.slice(0, this._selStart)
    const selected = v.slice(this._selStart, this._selEnd)
    const after    = v.slice(this._selEnd)
    const wrapped  = "// " + selected + " //"
    f.value = before + wrapped + after
    // With a selection, put the caret after the closing "//"; with none, put
    // it between the markers so the user can type the instruction there.
    const caret = selected ? (before + wrapped).length : (before + "// ").length
    f.focus()
    try { f.setSelectionRange(caret, caret) } catch (_) {}
    f.dispatchEvent(new Event("input",  { bubbles: true }))
    f.dispatchEvent(new Event("change", { bubbles: true }))
    this._remember(f)
  }

  close() { this._dismissed = true; this._hide() }

  _isEligible(el) {
    if (!el) return false
    if (el.tagName === "TEXTAREA") return true
    if (el.tagName !== "INPUT") return false
    return TEXT_INPUT_TYPES.has((el.type || "").toLowerCase())
  }

  _remember(el) {
    try { this._selStart = el.selectionStart; this._selEnd = el.selectionEnd }
    catch (_) { this._selStart = this._selEnd = (el.value || "").length }
  }

  _show() { this._dismissed = false; this.element.classList.add("is-visible") }
  _hide() { this.element.classList.remove("is-visible") }
}
